/**
 * cs-cart-preview.js
 * Runs on the cart page / cart drawer. For line items that carry a
 * `_design_token` property, it reads the matching IndexedDB entry written by
 * custom-selectors.js and injects small design thumbnails next to the
 * placement name properties in the cart line item.
 */
(function () {
  'use strict';

  const DESIGNS_PREFIX = 'pcd_designs_';
  const _PCD_DB        = 'pcd-designs';
  const _PCD_STORE     = 'designs';

  function idbOpen() {
    return new Promise((res, rej) => {
      const r = indexedDB.open(_PCD_DB, 1);
      r.onupgradeneeded = e => e.target.result.createObjectStore(_PCD_STORE);
      r.onsuccess = e => res(e.target.result);
      r.onerror   = e => rej(e.target.error);
    });
  }

  function idbGet(key) {
    return idbOpen().then(db => new Promise((res, rej) => {
      const req = db.transaction(_PCD_STORE, 'readonly').objectStore(_PCD_STORE).get(key);
      req.onsuccess = e => res(e.target.result ?? null);
      req.onerror   = e => rej(e.target.error);
    }));
  }

  /**
   * Find all `.product-properties` / `.cart-item__details` elements
   * that contain a `_design_token` property line, then load the
   * corresponding IDB designs and render thumbnails.
   */
  async function renderPreviews() {
    // Selectors cover Dawn cart page, cart drawer, and cart notification
    const propertyLists = document.querySelectorAll(
      '.cart-item__details, .cart-drawer__item, .cart-notification-product'
    );

    for (const container of propertyLists) {
      // Find the token property line (hidden — we only use it to look up IDB)
      const tokenLine = [...container.querySelectorAll('*')].find(
        el => el.textContent.includes('_design_token')
      );
      if (!tokenLine) continue;

      // Extract token value (Dawn renders props as "Key: Value")
      const tokenMatch = tokenLine.textContent.match(/:\s*(.+)$/);
      if (!tokenMatch) continue;
      const token = tokenMatch[1].trim();

      // Don't inject twice
      if (container.dataset.csPreviewDone) continue;
      container.dataset.csPreviewDone = '1';

      let designs;
      try {
        designs = await idbGet(DESIGNS_PREFIX + token);
      } catch (_) {
        continue;
      }
      if (!designs) continue;

      // Hide the raw token property line (it's internal)
      tokenLine.closest('li, p, div, span') && (tokenLine.closest('li, p, div, span').style.display = 'none');

      // Find property lines for "Design *" and inject thumbnail next to them
      container.querySelectorAll('*').forEach(el => {
        const text = el.textContent.trim();
        if (!text.startsWith('Design ')) return;

        // Match placement name: "Design Left Chest: my-logo.png"
        const nameMatch = text.match(/^Design ([^:]+)/);
        if (!nameMatch) return;
        const placementName = nameMatch[1].trim();

        // Find the corresponding design in IDB by placementName
        const design = Object.values(designs).find(
          d => d.placementName === placementName
        );
        if (!design?.previewUrl?.startsWith('data:image')) return;
        if (el.dataset.csThumbDone) return;
        el.dataset.csThumbDone = '1';

        // Build the thumbnail
        const thumb = document.createElement('img');
        thumb.src   = design.previewUrl;
        thumb.alt   = placementName;
        thumb.style.cssText = [
          'display:inline-block',
          'width:2.8rem',
          'height:2.8rem',
          'object-fit:contain',
          'border-radius:4px',
          'border:1px solid rgba(0,0,0,.1)',
          'margin-right:.6rem',
          'vertical-align:middle',
          'background:#f5f5f5',
        ].join(';');

        el.prepend(thumb);
      });
    }
  }

  // Run on load and whenever the DOM changes (cart drawer opens, etc.)
  function init() {
    renderPreviews();
    const mo = new MutationObserver(renderPreviews);
    mo.observe(document.body, { childList: true, subtree: true });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
